import React, { useMemo } from "react";
import { Container, Sprite, Text } from "@pixi/react";
import { TextStyle, TextMetrics } from "pixi.js";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { formatToCustomLocale } from "../../../../utilities";
import {
  FONT_CONFIG,
  TEXT_STYLE_CONFIG,
} from "../../../../pixiSettings";
import { getNewUITexture } from "../../../../assets";
import { selectBet, selectBets, selectCurrency } from "../../../../State/selectors";
import BonusButtonDecrementBet from "../Buttons/BonusButtonDecrementBet";
import BonusButtonIncrementBet from "../Buttons/BonusButtonIncrementBet";

// Reference dimensions at 800px popup width
// All values scale uniformly with popupScale
const REFERENCE_CONFIG = {
  // Background height (at reference scale=1). Width is derived from texture AR.
  backgroundHeight: 110,
  backgroundPaddingX: 80, // Horizontal padding inside background

  // Button size matches background height for perfect alignment
  buttonSize: 110,

  // Gap between buttons and background (also used for label gap above background)
  gap: 25,

  // Font sizes
  labelFontSize: 64,
  valueFontSize: 52,
};

const BonusBetSelector = ({ popupScale, position }) => {
  const { t } = useTranslation();
  const bet = useSelector(selectBet);
  const bets = useSelector(selectBets);
  const currency = useSelector(selectCurrency);

  // Background texture — drives width via native aspect ratio
  const bgTexture = useMemo(() => getNewUITexture("bonus_card_bet_display_background"), []);

  // Scale all dimensions uniformly based on popup scale
  const scaledConfig = useMemo(() => {
    return {
      backgroundHeight: Math.round(REFERENCE_CONFIG.backgroundHeight * popupScale),
      backgroundPaddingX: Math.round(REFERENCE_CONFIG.backgroundPaddingX * popupScale),
      buttonSize: Math.round(REFERENCE_CONFIG.buttonSize * popupScale),
      gap: Math.round(REFERENCE_CONFIG.gap * popupScale),
      labelFontSize: Math.round(REFERENCE_CONFIG.labelFontSize * popupScale),
      valueFontSize: Math.round(REFERENCE_CONFIG.valueFontSize * popupScale),
    };
  }, [popupScale]);

  // Prepare text content
  const betLabelText = t("bet").toUpperCase();
  const formattedBet = formatToCustomLocale(bet);
  const betAmountText = `${formattedBet} ${currency}`;

  // Text styles
  const betLabelStyle = useMemo(
    () =>
      new TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: scaledConfig.labelFontSize,
        fontWeight: "700",
        fill: 0xffffff,
        align: "center",
        ...TEXT_STYLE_CONFIG.OUTLINE,
      }),
    [scaledConfig.labelFontSize]
  );

  // Calculate max bet width based on largest bet in bets array
  const maxBetWidth = useMemo(() => {
    if (!bets || bets.length === 0) {
      const tempStyle = new TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: scaledConfig.valueFontSize,
        fontWeight: "700",
      });
      return TextMetrics.measureText(betAmountText, tempStyle).width;
    }

    const maxBet = Math.max(...bets);
    const maxBetText = `${formatToCustomLocale(maxBet)} ${currency}`;
    const tempStyle = new TextStyle({
      fontFamily: FONT_CONFIG.PRIMARY,
      fontSize: scaledConfig.valueFontSize,
      fontWeight: "700",
    });
    const maxBetMetrics = TextMetrics.measureText(maxBetText, tempStyle);

    return maxBetMetrics.width;
  }, [bets, currency, scaledConfig.valueFontSize, betAmountText]);

  // Auto-scale font size if actual bet text exceeds max width
  const finalBetFontSize = useMemo(() => {
    const tempStyle = new TextStyle({
      fontFamily: FONT_CONFIG.PRIMARY,
      fontSize: scaledConfig.valueFontSize,
      fontWeight: "700",
    });
    const metrics = TextMetrics.measureText(betAmountText, tempStyle);

    // If text exceeds max width, scale down proportionally
    if (metrics.width > maxBetWidth) {
      const scale = maxBetWidth / metrics.width;
      return Math.round(scaledConfig.valueFontSize * scale);
    }

    return scaledConfig.valueFontSize;
  }, [betAmountText, scaledConfig.valueFontSize, maxBetWidth]);

  const betAmountStyle = useMemo(
    () =>
      new TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: finalBetFontSize,
        fontWeight: "700",
        fill: 0xffffff,
        align: "center",
        ...TEXT_STYLE_CONFIG.OUTLINE,
      }),
    [finalBetFontSize]
  );

  // Measure text dimensions for positioning
  const labelMetrics = TextMetrics.measureText(betLabelText, betLabelStyle);

  // Background width: driven by texture AR when available, otherwise text + padding
  const backgroundWidth = useMemo(() => {
    if (bgTexture) {
      // Derive width from the texture's native aspect ratio to avoid distortion
      const ar = bgTexture.width / bgTexture.height;
      return Math.round(scaledConfig.backgroundHeight * ar);
    }
    return Math.round(
      Math.max(labelMetrics.width, maxBetWidth) + scaledConfig.backgroundPaddingX * 2
    );
  }, [bgTexture, scaledConfig.backgroundHeight, scaledConfig.backgroundPaddingX, labelMetrics.width, maxBetWidth]);

  // Text positioning
  // Label is above background with same gap as buttons
  const labelY = Math.round(-scaledConfig.backgroundHeight / 2 - scaledConfig.gap - labelMetrics.height / 2);

  // Value is centered vertically inside the background
  const amountY = 0;

  // Button positioning - buttons are same height as background, aligned vertically centered
  const totalWidth = scaledConfig.buttonSize + scaledConfig.gap + backgroundWidth + scaledConfig.gap + scaledConfig.buttonSize;

  const decrementX = Math.round(-totalWidth / 2 + scaledConfig.buttonSize / 2);
  const incrementX = Math.round(totalWidth / 2 - scaledConfig.buttonSize / 2);
  const buttonsY = 0; // Centered vertically with background

  const containerX = position?.x ?? 0;
  const containerY = position?.y ?? 0;

  return (
    <Container name="bonus_bet_selector" x={containerX} y={containerY} zIndex={5}>
      {/* Background sprite from new UI spritesheet */}
      {bgTexture && (
        <Sprite
          texture={bgTexture}
          anchor={0.5}
          width={backgroundWidth}
          height={scaledConfig.backgroundHeight}
        />
      )}

      {/* Label text */}
      <Text
        text={betLabelText}
        style={betLabelStyle}
        anchor={{ x: 0.5, y: 0.5 }}
        x={0}
        y={labelY}
        resolution={2}
      />

      {/* Value text */}
      <Text
        text={betAmountText}
        style={betAmountStyle}
        anchor={{ x: 0.5, y: 0.5 }}
        x={0}
        y={amountY}
        resolution={2}
      />

      {/* Decrement button - left side, vertically centered */}
      <BonusButtonDecrementBet
        x={decrementX}
        y={buttonsY}
        size={scaledConfig.buttonSize}
      />

      {/* Increment button - right side, vertically centered */}
      <BonusButtonIncrementBet
        x={incrementX}
        y={buttonsY}
        size={scaledConfig.buttonSize}
      />
    </Container>
  );
};

export default BonusBetSelector;
